import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../api';
import { formatRelativeDate } from '../utils/dateFormat';
import { APP_ORIGIN } from '../utils/appUrl';
import Toast from '../components/Toast';
import CowLoader from '../components/CowLoader';
import CowAvatar from '../components/CowAvatar';

function copyText(text) {
  if (navigator.clipboard) return navigator.clipboard.writeText(text);
  const el = document.createElement('textarea');
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export default function VaquitaDetails() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newContribution, setNewContribution] = useState({ amount: '', reason: '' });
  const [beneficiaryMode, setBeneficiaryMode] = useState('all'); // 'all' | 'select'
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState([]);
  const [editingContributionFor, setEditingContributionFor] = useState(null); // contributionId
  const [editContributionData, setEditContributionData] = useState({ amount: '', reason: '', beneficiaryIds: [] });
  const [receiptFile, setReceiptFile] = useState(null);
  const fileInputRef = useRef(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showPaymentSummary, setShowPaymentSummary] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [closingVaquita, setClosingVaquita] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [reopeningVaquita, setReopeningVaquita] = useState(false);
  const [debts, setDebts] = useState([]);
  const [toast, setToast] = useState(null);
  const [copyLabel, setCopyLabel] = useState('Copiar');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [bankCopied, setBankCopied] = useState(null);
  const [payModal, setPayModal] = useState(null); // { debtId, mode: 'debtor'|'lender' }
  const [payProofFile, setPayProofFile] = useState(null);
  const [payReceiveDate, setPayReceiveDate] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [friendIds, setFriendIds] = useState([]);
  const [activeTab, setActiveTab] = useState(1); // 0=Integrantes, 1=Aportes, 2=Muro
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [eligibleFriends, setEligibleFriends] = useState([]);
  const [inviteSent, setInviteSent] = useState({});

  const showToast = (message, type = 'success') => setToast({ message, type });
  const clearToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    const fetchVaquitaDetails = async () => {
      try {
        console.log("Intentando obtener detalles de vaquita con código:", inviteCode);
        const response = await api.get(`/vaquita/code/${inviteCode}`);
        console.log("Respuesta recibida:", response.data);
        setDetails(response.data);
      } catch (err) {
        console.error('Error completo:', err);
        const errorMessage = err.response?.data || err.message || 'Error desconocido';
        console.error('Mensaje de error:', errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (inviteCode) {
      fetchVaquitaDetails();
    } else {
      setError('No se proporcionó un código de invitación');
      setLoading(false);
    }
  }, [inviteCode]);

  useEffect(() => {
    api.get('/friends').then(r => setFriendIds(r.data.map(f => f.userId))).catch(() => {});
  }, []);

  // Si la vaquita está cerrada, cargar resumen y deudas
  useEffect(() => {
    if (details?.vaquita?.status === 'CLOSED') {
      api.get(`/vaquita/${details.vaquita.id}/debts`)
        .then(r => setDebts(r.data))
        .catch(() => {});
    }
  }, [details]);

  const handleContribute = async (e) => {
    e.preventDefault();
    const rawAmount = parseInt(newContribution.amount.replace(/\./g, ''), 10);
    if (!rawAmount || isNaN(rawAmount) || rawAmount <= 0) {
      showToast('Por favor ingresa un monto válido', 'error');
      return;
    }

    const beneficiaryIds = beneficiaryMode === 'select' ? selectedBeneficiaries : [];

    try {
      const response = await api.post(`/vaquita/${details.vaquita.id}/contribute`, {
        contribution: { amount: rawAmount, reason: newContribution.reason },
        beneficiaryIds
      });

      const savedContributionId = response.data.savedContributionId;

      if (receiptFile && savedContributionId) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        await api.post(`/contributions/${savedContributionId}/receipt`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      const updated = await api.get(`/vaquita/code/${inviteCode}`);
      setDetails(updated.data);
      setNewContribution({ amount: '', reason: '' });
      setBeneficiaryMode('all');
      setSelectedBeneficiaries([]);
      setReceiptFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      showToast('¡Aporte registrado con éxito!');
    } catch (err) {
      console.error('Error al registrar el aporte:', err);
      showToast(err.response?.data?.message || err.response?.data || 'Error al registrar el aporte', 'error');
    }
  };

  const handleSaveContribution = async (contributionId) => {
    const rawAmount = parseInt(editContributionData.amount.replace(/\./g, ''), 10);
    if (!rawAmount || isNaN(rawAmount) || rawAmount <= 0) {
      showToast('Por favor ingresa un monto válido', 'error');
      return;
    }
    try {
      await api.put(`/contributions/${contributionId}`, { amount: rawAmount, reason: editContributionData.reason });
      await api.put(`/contributions/${contributionId}/beneficiaries`, editContributionData.beneficiaryIds);
      const updated = await api.get(`/vaquita/code/${inviteCode}`);
      setDetails(updated.data);
      setEditingContributionFor(null);
      showToast('Aporte actualizado');
    } catch (err) {
      showToast(err.response?.data || 'Error al actualizar el aporte', 'error');
    }
  };

  const handleDeleteContribution = async (contributionId) => {
    if (!window.confirm('¿Eliminar este aporte?')) return;
    try {
      await api.delete(`/contributions/${contributionId}`);
      const updated = await api.get(`/vaquita/code/${inviteCode}`);
      setDetails(updated.data);
      showToast('Aporte eliminado');
    } catch (err) {
      showToast('Error al eliminar el aporte', 'error');
    }
  };

  const handleCopyBankData = (debt) => {
    const parts = [
      debt.lenderBanco          ? `Banco: ${debt.lenderBanco}`                     : null,
      debt.lenderTipoCuenta     ? `Tipo de cuenta: ${debt.lenderTipoCuenta}`       : null,
      debt.lenderNumeroCuenta   ? `Número de cuenta: ${debt.lenderNumeroCuenta}`   : null,
      debt.lenderRut            ? `RUT: ${debt.lenderRut}`                         : null,
      debt.lenderNombreCompleto ? `Nombre: ${debt.lenderNombreCompleto}`           : `Nombre: ${debt.lenderName}`,
      debt.lenderEmail          ? `Correo: ${debt.lenderEmail}`                    : null,
    ].filter(Boolean);
    copyText(parts.join('\n'));
    setBankCopied(debt.id);
    setTimeout(() => setBankCopied(null), 2000);
  };

  const handleMarkPaid = async (debtId) => {
    try {
      await api.post(`/personal-debts/${debtId}/pay`);
      setDebts(prev => prev.map(d => d.id === debtId ? { ...d, status: 'Pagada' } : d));
      showToast('Deuda confirmada como pagada');
    } catch (err) {
      showToast('Error al confirmar', 'error');
    }
  };

  const handleDebtorPaid = async () => {
    setPaySubmitting(true);
    try {
      const fd = new FormData();
      if (payProofFile) fd.append('file', payProofFile);
      await api.post(`/personal-debts/${payModal.debtId}/debtor-paid`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDebts(prev => prev.map(d => d.id === payModal.debtId ? { ...d, status: 'PendienteConfirmacion' } : d));
      setPayModal(null); setPayProofFile(null);
      showToast('¡Pago enviado! Esperando confirmación.');
    } catch { showToast('Error al enviar pago', 'error'); }
    finally { setPaySubmitting(false); }
  };

  const handleLenderConfirm = async () => {
    setPaySubmitting(true);
    try {
      const params = payReceiveDate ? `?receivedAt=${payReceiveDate}` : '';
      await api.post(`/personal-debts/${payModal.debtId}/confirm${params}`);
      setDebts(prev => prev.map(d => d.id === payModal.debtId ? { ...d, status: 'Pagada' } : d));
      setPayModal(null); setPayReceiveDate('');
      showToast('Pago confirmado');
    } catch { showToast('Error al confirmar', 'error'); }
    finally { setPaySubmitting(false); }
  };

  const handleLenderDeny = async (debtId) => {
    try {
      await api.post(`/personal-debts/${debtId}/deny`);
      setDebts(prev => prev.map(d => d.id === debtId ? { ...d, status: 'Pendiente' } : d));
      showToast('Pago rechazado, deuda restablecida');
    } catch { showToast('Error', 'error'); }
  };

  const handleCloseVaquita = async () => {
    setClosingVaquita(true);
    setShowCloseConfirm(false);
    try {
      const response = await api.post(`/vaquita/${details.vaquita.id}/close`);
      setPaymentSummary(response.data);
      setShowPaymentSummary(true);
      const updated = await api.get(`/vaquita/code/${inviteCode}`);
      setDetails(updated.data);
      const debtsRes = await api.get(`/vaquita/${updated.data.vaquita.id}/debts`);
      setDebts(debtsRes.data);
      showToast('Vaquita cerrada');
    } catch (err) {
      showToast(err.response?.data || 'Error al cerrar la vaquita', 'error');
    } finally {
      setClosingVaquita(false);
    }
  };

  const handleReopenVaquita = async () => {
    setReopeningVaquita(true);
    setShowReopenConfirm(false);
    try {
      const updated = await api.post(`/vaquita/${details.vaquita.id}/reopen`);
      setDetails(updated.data);
      setPaymentSummary(null);
      setDebts([]);
      showToast('Vaquita reabierta');
    } catch (err) {
      showToast(err.response?.data || 'Error al reabrir la vaquita', 'error');
    } finally {
      setReopeningVaquita(false);
    }
  };

  if (loading) return <CowLoader text="Cargando vaquita..." />;
  if (error) {
    return (
      <div className="error-container">
        <h2>Error al cargar la vaquita</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/my-vaquitas')}>
          Volver a mis vaquitas
        </button>
      </div>
    );
  }
  if (!details) return <p>No se encontró la vaquita</p>;

  const { vaquita, participants, contributions, balances, totalContributed, userRole } = details;

  return (
    <div className="vaquita-details-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}
      <div className="vaquita-card">
        <div className="vaquita-header">
          <div className="vaquita-title">
            <h1>{vaquita.name} 🐮</h1>
            {userRole === 'CREADOR' && <span className="admin-badge">Admin</span>}
            {vaquita.status === 'CLOSED' && <span className="closed-badge">Cerrada</span>}
          </div>
          <div className="header-actions">
            {vaquita.status === 'CLOSED' && (
              <button className="payment-summary-button" onClick={async () => {
                if (!paymentSummary) {
                  try {
                    const r = await api.get(`/vaquita/${vaquita.id}/payment-summary`);
                    setPaymentSummary(r.data);
                  } catch { setPaymentSummary([]); }
                }
                setShowPaymentSummary(true);
              }}>
                Ver repartición final
              </button>
            )}
            {userRole === 'CREADOR' && vaquita.status === 'ACTIVE' && (
              <button
                onClick={() => setShowCloseConfirm(true)}
                className="close-vaquita-button"
                disabled={closingVaquita}
              >
                {closingVaquita ? 'Cerrando...' : 'Cerrar Vaquita'}
              </button>
            )}
            {userRole === 'CREADOR' && vaquita.status === 'CLOSED' && (
              <button
                onClick={() => setShowReopenConfirm(true)}
                className="reopen-vaquita-button"
                disabled={reopeningVaquita}
              >
                {reopeningVaquita ? 'Reabriendo...' : 'Reabrir Vaquita'}
              </button>
            )}
          </div>
        </div>
        <p className="description">{vaquita.description}</p>
        <div className="stats-grid">
          <div className="stat-card">
            <h3 className="stat-label">Total Recaudado</h3>
            <p className="stat-value">${totalContributed.toLocaleString('es-CL', {maximumFractionDigits:0})}</p>
          </div>
          <div className="stat-card">
            <h3 className="stat-label">Participantes</h3>
            <p className="stat-value">{participants.length}</p>
          </div>
          <div className="stat-card">
            <h3 className="stat-label">Promedio por persona</h3>
            <p className="stat-value">
              ${participants.length > 0 ? (totalContributed / participants.length).toLocaleString('es-CL', {maximumFractionDigits:0}) : '0'}
            </p>
          </div>
        </div>
        
        <div className="invite-code">
          <span className="code-text">{vaquita.inviteCode}</span>
          <button
            className="copy-button"
            onClick={() => {
              copyText(vaquita.inviteCode);
              setCopyLabel('¡Copiado!');
              setTimeout(() => setCopyLabel('Copiar'), 2000);
            }}
          >
            {copyLabel}
          </button>
          <button
            className="copy-button share-link-btn"
            onClick={() => {
              copyText(`${APP_ORIGIN}/vaquita/preview/${vaquita.inviteCode}`);
              showToast('¡Link copiado!');
            }}
          >
            🔗 Link
          </button>
          <button
            className="copy-button share-link-btn"
            onClick={() => setShowQrModal(true)}
          >
            📱 QR
          </button>
        </div>
      </div>

      {/* Muro de la vergüenza — solo cuando está cerrada */}
      {vaquita.status === 'CLOSED' && (
        <section className="shame-wall">
          <h2 className="shame-title">💀 El Muro de la Vergüenza</h2>
          {debts.length === 0 ? (
            <p className="shame-subtitle">🎉 ¡Todos al día! No hay deudas pendientes.</p>
          ) : (
            <>
              <p className="shame-subtitle">Deudas pendientes para quedar a mano</p>
              <div className="debts-list">
                {debts.map(debt => (
                  <div key={debt.id} className={`debt-card ${debt.status === 'Pagada' ? 'debt-paid' : ''} ${debt.status === 'PendienteConfirmacion' ? 'debt-pending-confirm' : ''} ${debt.isCurrentUserBorrower ? 'debt-mine' : ''}`}>
                    <div className="debt-info">
                      <span className="debt-borrower">{debt.borrowerName}</span>
                      <span className="debt-arrow">debe pagarle a</span>
                      <span className="debt-lender">{debt.lenderName}</span>
                    </div>
                    <div className="debt-right">
                      <span className="debt-amount">${debt.amount.toLocaleString('es-CL', {maximumFractionDigits:0})}</span>

                      {debt.status === 'Pagada' && <span className="debt-status-paid">✓ Pagada</span>}

                      {debt.status === 'PendienteConfirmacion' && debt.isCurrentUserBorrower && (
                        <span className="debt-status-waiting">⏳ Esperando confirmación</span>
                      )}

                      {debt.status === 'PendienteConfirmacion' && debt.isCurrentUserLender && (
                        <div className="debt-actions-col">
                          <span className="debt-status-waiting">💰 {debt.borrowerName} dice que pagó</span>
                          <button className="pay-button" onClick={() => { setPayModal({ debtId: debt.id, mode: 'lender' }); setPayReceiveDate(''); }}>Confirmar recibo</button>
                          <button className="deny-button" onClick={() => handleLenderDeny(debt.id)}>Rechazar</button>
                        </div>
                      )}

                      {debt.status === 'Pendiente' && (
                        <>
                          {debt.isCurrentUserBorrower && debt.lenderBanco && (
                            <button className="bank-copy-btn" onClick={() => handleCopyBankData(debt)}>
                              {bankCopied === debt.id ? '¡Copiado!' : '🏦 Copiar datos'}
                            </button>
                          )}
                          {debt.isCurrentUserBorrower && (
                            <button className="pay-button debtor-pay" onClick={() => { setPayModal({ debtId: debt.id, mode: 'debtor' }); setPayProofFile(null); }}>
                              ✓ Ya pagué
                            </button>
                          )}
                          {debt.isCurrentUserLender && (
                            <button className="pay-button" onClick={() => { setPayModal({ debtId: debt.id, mode: 'lender' }); setPayReceiveDate(''); }}>
                              Confirmar recibo
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* 2 tabs */}
      <div className="tabs-bar">
        <button className={`tab-btn ${activeTab === 0 ? 'active' : ''}`} onClick={() => setActiveTab(0)}>
          <span className="tab-label">Integrantes</span>
          <span className="tab-pill">{participants.length}</span>
        </button>
        <button className={`tab-btn ${activeTab === 1 ? 'active' : ''}`} onClick={() => setActiveTab(1)}>
          <span className="tab-label">Aportes</span>
          <span className="tab-pill">{contributions.length}</span>
        </button>
      </div>

      {/* ── Tab 0: Integrantes ── */}
      {activeTab === 0 && (
        <section className="tab-content">
          {vaquita.status === 'ACTIVE' && (
            <button className="invite-friends-btn" onClick={async () => {
              const res = await api.get(`/vaquita-invitations/eligible/${vaquita.id}`);
              setEligibleFriends(res.data); setInviteSent({}); setShowInviteModal(true);
            }}>👥 Invitar amigos</button>
          )}
          {participants.length === 0 ? (
            <p className="empty-message">Aún no hay participantes.</p>
          ) : (
            <div className="participant-list">
              {participants.map(participant => (
                <div key={participant.id} className={`participant-card ${participant.userId === details.currentUserId ? 'current-user' : ''}`}>
                  <div className={`participant-avatar-wrap ${participant.userId !== details.currentUserId ? 'clickable' : ''}`}
                    onClick={() => participant.userId !== details.currentUserId && navigate(`/user/${participant.userId}`)}>
                    <CowAvatar seed={participant.profilePicture || participant.userId} size={38} />
                  </div>
                  <div className="participant-info">
                    <span className={`participant-name ${participant.userId !== details.currentUserId ? 'clickable-name' : ''}`}
                      onClick={() => participant.userId !== details.currentUserId && navigate(`/user/${participant.userId}`)}>
                      {participant.userName}
                      {participant.userId === details.currentUserId && <span className="current-user-badge">(Tú)</span>}
                      {participant.rol === 'CREADOR' && <span className="creator-badge">creador</span>}
                    </span>
                  </div>
                  <div className="participant-details">
                    <span className={`participant-amount ${balances[participant.userId] < 0 ? 'negative' : 'positive'}`}>
                      ${balances[participant.userId]?.toLocaleString('es-CL', {maximumFractionDigits:0}) || '0'}
                    </span>
                    <span className="amount-explanation">
                      {participant.userId === details.currentUserId
                        ? balances[participant.userId] < 0 ? 'Debes aportar para igualar' : 'Has aportado más que el promedio'
                        : balances[participant.userId] < 0 ? 'Debe aportar para igualar' : 'Ha aportado más que el promedio'}
                    </span>
                    <span className="join-date">
                      {participant.userId === details.currentUserId ? 'Te uniste ' : 'Se unió '}
                      {formatRelativeDate(participant.joinedAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="avg-row">
            Promedio por persona: <strong>${participants.length > 0 ? (totalContributed / participants.length).toLocaleString('es-CL', {maximumFractionDigits:0}) : '0'}</strong>
          </div>
        </section>
      )}

      {/* ── Tab 1: Aportes ── */}
      {activeTab === 1 && (
        <section className="tab-content">
          <div className="aporte-form-card">
            <h3 className="aporte-form-title">Agregar Aporte</h3>
            {vaquita.status === 'CLOSED' && (
              <p className="closed-notice">Esta vaquita está cerrada. No se pueden registrar más aportes.</p>
            )}
            <form onSubmit={handleContribute} className="contribution-form" style={vaquita.status === 'CLOSED' ? {opacity: 0.5, pointerEvents: 'none'} : {}}>
          <div className="form-group">
            <label htmlFor="amount">Monto</label>
            <div className="amount-input-group">
              <span className="currency-symbol">$</span>
              <input
                id="amount"
                type="text"
                inputMode="numeric"
                value={newContribution.amount}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                  const num = parseInt(digits, 10);
                  if (!digits) {
                    setNewContribution({ ...newContribution, amount: '' });
                    return;
                  }
                  const capped = Math.min(num, 999999);
                  const formatted = capped.toLocaleString('es-CL');
                  setNewContribution({ ...newContribution, amount: formatted });
                }}
                placeholder="0"
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="reason">Razón (opcional)</label>
            <input
              id="reason"
              type="text"
              value={newContribution.reason}
              onChange={(e) => setNewContribution({
                ...newContribution,
                reason: e.target.value
              })}
              placeholder="Ej: Para la comida"
            />
          </div>
          <div className="form-group">
            <label>¿Quién comparte este gasto?</label>
            <div className="beneficiary-toggle">
              <button
                type="button"
                className={`toggle-btn ${beneficiaryMode === 'all' ? 'active' : ''}`}
                onClick={() => setBeneficiaryMode('all')}
              >
                Todos
              </button>
              <button
                type="button"
                className={`toggle-btn ${beneficiaryMode === 'select' ? 'active' : ''}`}
                onClick={() => { setBeneficiaryMode('select'); setSelectedBeneficiaries(participants.map(p => p.userId)); }}
              >
                Seleccionar personas
              </button>
            </div>
            {beneficiaryMode === 'select' && (
              <div className="beneficiary-checkboxes">
                <label className="beneficiary-checkbox-label select-all-label">
                  <input
                    type="checkbox"
                    checked={selectedBeneficiaries.length === participants.length}
                    onChange={e => setSelectedBeneficiaries(e.target.checked ? participants.map(p => p.userId) : [])}
                  />
                  <strong>{selectedBeneficiaries.length === participants.length ? 'Deseleccionar todos' : 'Seleccionar todos'}</strong>
                </label>
                {[...participants].sort((a, b) => {
                  const aFriend = friendIds.includes(a.userId);
                  const bFriend = friendIds.includes(b.userId);
                  if (aFriend && !bFriend) return -1;
                  if (!aFriend && bFriend) return 1;
                  return 0;
                }).map(p => (
                  <label key={p.userId} className="beneficiary-checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedBeneficiaries.includes(p.userId)}
                      onChange={e => {
                        if (e.target.checked) setSelectedBeneficiaries(prev => [...prev, p.userId]);
                        else setSelectedBeneficiaries(prev => prev.filter(id => id !== p.userId));
                      }}
                    />
                    {p.userName}
                    {p.userId === details.currentUserId && ' (Tú)'}
                    {friendIds.includes(p.userId) && ' 👥'}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="receipt">Boleta / comprobante (opcional)</label>
            <div className="receipt-input-area">
              <input
                id="receipt"
                type="file"
                accept="image/*,application/pdf"
                ref={fileInputRef}
                onChange={e => setReceiptFile(e.target.files[0] || null)}
                className="file-input"
              />
              {receiptFile && (
                <span className="file-selected">📎 {receiptFile.name}</span>
              )}
            </div>
          </div>
          <button type="submit" className="submit-button">
            Registrar Aporte 💰
          </button>
        </form>
          </div>{/* /aporte-form-card */}

          <div className="avg-row">
            Promedio por persona: <strong>${participants.length > 0 ? (totalContributed / participants.length).toLocaleString('es-CL', {maximumFractionDigits:0}) : '0'}</strong>
          </div>

          {contributions.length === 0 ? (
            <p className="empty-message">Aún no hay aportes registrados.</p>
          ) : (
          <div className="contributions-list">
            {contributions
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map(contribution => {
                const participant = participants.find(p => p.userId === contribution.userId);
                return (
                  <div key={contribution.id} className="contribution-card">
                    <div className="contribution-header">
                      <span className="contribution-amount">${contribution.amount.toLocaleString('es-CL', {maximumFractionDigits:0})}</span>
                      <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                        <span className="contribution-date">{formatRelativeDate(contribution.createdAt)}</span>
                        {(contribution.userId === details.currentUserId || userRole === 'CREADOR') && vaquita.status === 'ACTIVE' && (
                          <button className="edit-beneficiaries-btn" onClick={() => {
                            setEditingContributionFor(contribution.id);
                            setEditContributionData({
                              amount: contribution.amount.toLocaleString('es-CL'),
                              reason: contribution.reason || '',
                              beneficiaryIds: contribution.beneficiaryIds?.length > 0 ? [...contribution.beneficiaryIds] : participants.map(p => p.userId)
                            });
                          }}>✏️</button>
                        )}
                        {userRole === 'CREADOR' && vaquita.status === 'ACTIVE' && (
                          <button className="reject-btn" onClick={() => handleDeleteContribution(contribution.id)}>🗑</button>
                        )}
                      </div>
                    </div>
                    <div className="contribution-details">
                      <div className="contributor-row">
                        <CowAvatar seed={participant?.profilePicture || contribution.userId} size={26} />
                        <span className="contributor-name">{participant?.userName || 'Usuario'}</span>
                      </div>
                      {contribution.reason && (
                        <span className="contribution-reason">"{contribution.reason}"</span>
                      )}
                    </div>
                    {contribution.beneficiaryIds?.length > 0 && contribution.beneficiaryIds.length < participants.length && (
                      <div className="beneficiary-chips">
                        <span className="beneficiary-chips-label">Comparte con:</span>
                        {contribution.beneficiaryIds.map(bid => {
                          const p = participants.find(p => p.userId === bid);
                          return <span key={bid} className="beneficiary-chip">{p?.userName || bid}</span>;
                        })}
                      </div>
                    )}
                    {contribution.receiptUrl && (
                      <div className="receipt-row">
                        <button className="receipt-thumb-btn" onClick={() => setLightboxUrl('/api' + contribution.receiptUrl)}>
                          🧾 Ver boleta
                        </button>
                      </div>
                    )}
                    {editingContributionFor === contribution.id && (
                      <div className="edit-beneficiaries-panel">
                        <p className="edit-beneficiaries-title">Editar aporte</p>
                        <div className="form-group">
                          <label htmlFor={`edit-amount-${contribution.id}`}>Monto</label>
                          <div className="amount-input-group">
                            <span className="currency-symbol">$</span>
                            <input
                              id={`edit-amount-${contribution.id}`}
                              type="text"
                              inputMode="numeric"
                              value={editContributionData.amount}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                                if (!digits) {
                                  setEditContributionData(prev => ({ ...prev, amount: '' }));
                                  return;
                                }
                                const capped = Math.min(parseInt(digits, 10), 999999);
                                setEditContributionData(prev => ({ ...prev, amount: capped.toLocaleString('es-CL') }));
                              }}
                              placeholder="0"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor={`edit-reason-${contribution.id}`}>Razón (opcional)</label>
                          <input
                            id={`edit-reason-${contribution.id}`}
                            type="text"
                            value={editContributionData.reason}
                            onChange={(e) => setEditContributionData(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder="Ej: Para la comida"
                          />
                        </div>
                        <p className="edit-beneficiaries-title">¿Quién comparte este gasto?</p>
                        <div className="beneficiary-checkboxes">
                          <label className="beneficiary-checkbox-label select-all-label">
                            <input
                              type="checkbox"
                              checked={editContributionData.beneficiaryIds.length === participants.length}
                              onChange={e => setEditContributionData(prev => ({
                                ...prev,
                                beneficiaryIds: e.target.checked ? participants.map(p => p.userId) : []
                              }))}
                            />
                            <strong>{editContributionData.beneficiaryIds.length === participants.length ? 'Deseleccionar todos' : 'Seleccionar todos'}</strong>
                          </label>
                          {participants.map(p => (
                            <label key={p.userId} className="beneficiary-checkbox-label">
                              <input
                                type="checkbox"
                                checked={editContributionData.beneficiaryIds.includes(p.userId)}
                                onChange={e => {
                                  setEditContributionData(prev => ({
                                    ...prev,
                                    beneficiaryIds: e.target.checked
                                      ? [...prev.beneficiaryIds, p.userId]
                                      : prev.beneficiaryIds.filter(id => id !== p.userId)
                                  }));
                                }}
                              />
                              {p.userName}{p.userId === details.currentUserId && ' (Tú)'}
                            </label>
                          ))}
                        </div>
                        <div style={{display:'flex', gap:'0.5rem', marginTop:'0.75rem'}}>
                          <button className="submit-button" style={{padding:'0.5rem 1rem', fontSize:'0.9rem'}}
                            onClick={() => handleSaveContribution(contribution.id)}>
                            Guardar
                          </button>
                          <button style={{padding:'0.5rem 1rem', fontSize:'0.9rem', background:'var(--bg-primary)', border:'1px solid var(--border-color)', borderRadius:'4px', cursor:'pointer', color:'var(--text-primary)'}}
                            onClick={() => setEditingContributionFor(null)}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
          )}
        </section>
      )}{/* /Tab 1 */}

      {/* Invite modal — fuera de los tabs */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="invite-modal" onClick={e => e.stopPropagation()}>
            <div className="invite-modal-header">
              <h3>Invitar amigos</h3>
              <button className="modal-close-btn" onClick={() => setShowInviteModal(false)}>✕</button>
            </div>
            {eligibleFriends.length === 0 ? (
              <p className="invite-empty">Todos tus amigos ya están en esta vaquita o no tienes amigos agregados aún.</p>
            ) : (
              <div className="invite-list">
                {eligibleFriends.map(f => (
                  <div key={f.id} className="invite-friend-row">
                    <CowAvatar seed={f.profilePicture || f.id} size={36} />
                    <span className="invite-friend-name">{f.name}</span>
                    {inviteSent[f.id] ? (
                      <span className="invite-sent-label">Invitación enviada ✓</span>
                    ) : (
                      <button className="invite-send-btn" onClick={async () => {
                        await api.post(`/vaquita-invitations/invite?vaquitaId=${vaquita.id}&userId=${f.id}`);
                        setInviteSent(prev => ({ ...prev, [f.id]: true }));
                      }}>Invitar</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal QR */}
      {showQrModal && (
        <div className="modal-overlay" onClick={() => setShowQrModal(false)}>
          <div className="qr-modal" onClick={e => e.stopPropagation()}>
            <div className="invite-modal-header">
              <h3>Código QR</h3>
              <button className="modal-close-btn" onClick={() => setShowQrModal(false)}>✕</button>
            </div>
            <div className="qr-code-wrap">
              <QRCodeSVG value={`${APP_ORIGIN}/vaquita/preview/${vaquita.inviteCode}`} size={220} />
            </div>
            <button
              className="copy-button"
              onClick={() => {
                copyText(`${APP_ORIGIN}/vaquita/preview/${vaquita.inviteCode}`);
                showToast('¡Link copiado!');
              }}
            >
              Copiar link
            </button>
          </div>
        </div>
      )}

      {/* Modal pago */}
      {payModal && (
        <div className="modal-overlay" onClick={() => setPayModal(null)}>
          <div className="pay-modal" onClick={e => e.stopPropagation()}>
            {payModal.mode === 'debtor' ? (
              <>
                <h3 className="pay-modal-title">Confirmar que pagué</h3>
                <p className="pay-modal-hint">Sube una imagen del comprobante si tienes (opcional).</p>
                <label className="pay-proof-label">
                  📎 {payProofFile ? payProofFile.name : 'Seleccionar comprobante'}
                  <input type="file" accept="image/*" style={{display:'none'}} onChange={e => setPayProofFile(e.target.files[0] || null)} />
                </label>
                <div className="pay-modal-actions">
                  <button className="pay-modal-btn confirm" onClick={handleDebtorPaid} disabled={paySubmitting}>
                    {paySubmitting ? 'Enviando...' : 'Confirmar pago'}
                  </button>
                  <button className="pay-modal-btn cancel" onClick={() => setPayModal(null)}>Cancelar</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="pay-modal-title">Confirmar recibo de pago</h3>
                <p className="pay-modal-hint">¿Cuándo recibiste el pago? (opcional, por defecto ahora)</p>
                <input
                  type="datetime-local"
                  className="pay-date-input"
                  value={payReceiveDate}
                  onChange={e => setPayReceiveDate(e.target.value)}
                />
                <div className="pay-modal-actions">
                  <button className="pay-modal-btn confirm" onClick={handleLenderConfirm} disabled={paySubmitting}>
                    {paySubmitting ? 'Confirmando...' : 'Confirmar recibo'}
                  </button>
                  <button className="pay-modal-btn cancel" onClick={() => setPayModal(null)}>Cancelar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lightbox boleta */}
      {lightboxUrl && (
        <div className="modal-overlay" onClick={() => setLightboxUrl(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="close-button" style={{position:'absolute',top:'0.5rem',right:'0.75rem'}} onClick={() => setLightboxUrl(null)}>×</button>
            {lightboxUrl.match(/\.pdf$/i)
              ? <iframe src={lightboxUrl} title="Boleta" style={{width:'80vw',height:'80vh',border:'none'}} />
              : <img src={lightboxUrl} alt="Boleta" style={{maxWidth:'90vw',maxHeight:'85vh',borderRadius:'8px',objectFit:'contain'}} />
            }
          </div>
        </div>
      )}

      {/* Modal de confirmación de cierre */}
      {showCloseConfirm && (
        <div className="modal-overlay">
          <div className="modal-content" style={{maxWidth: '420px'}}>
            <div className="modal-header">
              <h2>Cerrar Vaquita</h2>
              <button className="close-button" onClick={() => setShowCloseConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6'}}>
                Al cerrar la vaquita se calculará quién le debe pagar a quién para que todos queden a mano. <strong>No se podrán registrar más aportes.</strong>
              </p>
              <div style={{display: 'flex', gap: '1rem'}}>
                <button className="submit-button" onClick={handleCloseVaquita} style={{flex: 1}}>
                  Sí, cerrar
                </button>
                <button onClick={() => setShowCloseConfirm(false)} style={{flex: 1, padding: '1rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '1rem'}}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de reapertura */}
      {showReopenConfirm && (
        <div className="modal-overlay">
          <div className="modal-content" style={{maxWidth: '420px'}}>
            <div className="modal-header">
              <h2>Reabrir Vaquita</h2>
              <button className="close-button" onClick={() => setShowReopenConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              {debts.some(d => d.status !== 'Pendiente') ? (
                <p style={{color: 'var(--danger-color)', marginBottom: '1.5rem', lineHeight: '1.6'}}>
                  <strong>Ojo:</strong> algunas deudas de esta vaquita ya tienen un pago marcado o confirmado. Al reabrir, esas deudas se eliminarán junto con ese registro y deberán coordinar el pago aparte.
                </p>
              ) : (
                <p style={{color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6'}}>
                  Se volverá a estado activo y se eliminará la repartición de deudas calculada al cerrar. Podrás editar o agregar aportes de nuevo.
                </p>
              )}
              <div style={{display: 'flex', gap: '1rem'}}>
                <button className="submit-button" onClick={handleReopenVaquita} style={{flex: 1}}>
                  Sí, reabrir
                </button>
                <button onClick={() => setShowReopenConfirm(false)} style={{flex: 1, padding: '1rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '1rem'}}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de resumen de pagos */}
      {showPaymentSummary && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Repartición Final</h2>
              <button 
                className="close-button"
                onClick={() => setShowPaymentSummary(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {paymentSummary && paymentSummary.length > 0 ? (
                <>
                  <div className="summary-explanation">
                    <p>Para igualar los aportes entre todos los participantes, se deben realizar los siguientes pagos:</p>
                  </div>
                  <div className="payments-list">
                    {paymentSummary.map((payment, index) => (
                      <div key={index} className="payment-item">
                        <div className="payment-details">
                          <div className="payment-users">
                            <div className="user-box from-user">
                              <span className="user-emoji">💸</span>
                              <div className="user-info">
                                <span className="user-name">{payment.fromUserName || payment.fromName || '—'}</span>
                                <span className="payment-label">debe pagar</span>
                              </div>
                            </div>
                            <div className="payment-amount-box">
                              <span className="payment-amount">${payment.amount.toLocaleString('es-CL', {maximumFractionDigits:0})}</span>
                              <span className="payment-arrow">➡️</span>
                            </div>
                            <div className="user-box to-user">
                              <span className="user-emoji">💰</span>
                              <div className="user-info">
                                <span className="user-name">{payment.toUserName || payment.toName || '—'}</span>
                                <span className="payment-label">debe recibir</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="summary-footer">
                    <p className="summary-note">
                      Nota: Este cálculo minimiza el número de transacciones necesarias para igualar los aportes.
                    </p>
                  </div>
                </>
              ) : (
                <div className="no-payments">
                  <p>No hay pagos pendientes</p>
                  <p className="sub-text">Todos los participantes han aportado cantidades similares.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .vaquita-details-container {
          padding: 1rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        @media (min-width: 768px) {
          .vaquita-details-container { padding: 2rem; }
        }

        .vaquita-card {
          background: var(--bg-secondary);
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
          box-shadow: 0 4px 12px var(--shadow-color);
          transition: box-shadow 0.3s ease;
        }

        @media (min-width: 768px) {
          .vaquita-card { padding: 2rem; margin-bottom: 2rem; }
        }

        .vaquita-card:hover {
          box-shadow: 0 6px 16px var(--shadow-color);
        }

        .vaquita-header {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        @media (min-width: 768px) {
          .vaquita-header { margin-bottom: 2rem; }
        }

        .vaquita-title {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .vaquita-title h1 {
          font-size: 1.4rem;
          color: var(--text-primary);
          margin: 0;
          word-break: break-word;
        }

        @media (min-width: 768px) {
          .vaquita-title h1 { font-size: 2rem; }
        }

        .admin-badge {
          background: var(--accent-color);
          color: white;
          padding: 0.2rem 0.5rem;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: normal;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        @media (min-width: 768px) {
          .stats-grid { grid-template-columns: repeat(3, 1fr); gap: 2rem; margin-bottom: 2rem; }
        }

        .stat-card {
          background: var(--bg-primary);
          padding: 0.75rem;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 2px 8px var(--shadow-color);
          transition: box-shadow 0.2s ease;
        }

        @media (min-width: 768px) {
          .stat-card { padding: 1.5rem; }
        }

        .stat-card:hover {
          box-shadow: 0 4px 12px var(--shadow-color);
        }

        .stat-label {
          color: var(--text-muted);
          margin-bottom: 0.25rem;
          font-size: 0.75rem;
        }

        .stat-value {
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: bold;
          word-break: break-all;
        }

        @media (min-width: 768px) {
          .stat-label { font-size: 0.9rem; margin-bottom: 0.5rem; }
          .stat-value { font-size: 1.5rem; }
        }

        .invite-code {
          background: var(--bg-primary);
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .code-text {
          color: var(--text-secondary);
          font-family: monospace;
          font-size: 1.2rem;
        }

        .copy-button {
          background: var(--accent-color);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .copy-button:hover {
          background: var(--accent-hover);
        }

        /* ── Tabs ── */
        .tabs-bar {
          display: flex;
          background: var(--bg-secondary);
          border-radius: 12px;
          box-shadow: 0 2px 4px var(--shadow-color);
          overflow: hidden;
          margin-bottom: 0.75rem;
        }

        .tab-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          padding: 0.85rem 0.5rem;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 0.88rem;
          font-weight: 600;
          color: var(--text-muted);
          transition: color 0.15s, border-color 0.15s, background 0.15s;
          -webkit-tap-highlight-color: transparent;
        }

        .tab-btn:hover { background: var(--bg-primary); color: var(--text-secondary); }

        .tab-btn.active {
          color: var(--accent-color);
          border-bottom-color: var(--accent-color);
          background: var(--bg-primary);
        }

        .tab-pill {
          background: var(--bg-secondary);
          color: var(--text-muted);
          border: 1px solid var(--border-color);
          font-size: 0.7rem;
          font-weight: 700;
          border-radius: 999px;
          padding: 1px 7px;
          min-width: 20px;
          text-align: center;
        }

        .tab-btn.active .tab-pill {
          background: var(--accent-color);
          color: white;
          border-color: var(--accent-color);
        }

        .tab-content {
          background: var(--bg-secondary);
          border-radius: 12px;
          padding: 1.25rem;
          box-shadow: 0 2px 4px var(--shadow-color);
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        @media (min-width: 768px) {
          .tab-content { padding: 2rem; }
        }

        .avg-row {
          font-size: 0.88rem;
          color: var(--text-muted);
          padding: 0.5rem 0;
          border-top: 1px solid var(--border-color);
        }

        .avg-row strong { color: var(--text-primary); }

        /* Aporte form card */
        .aporte-form-card {
          background: var(--bg-primary);
          border-radius: 10px;
          padding: 1rem;
        }

        .aporte-form-title {
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: 700;
          margin: 0 0 1rem;
        }

        /* Shame tab */
        .shame-tab { align-items: center; }

        .shame-locked {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          padding: 2rem 1rem;
          text-align: center;
        }

        .shame-skull { font-size: 3.5rem; }

        .shame-locked-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }

        .shame-locked-text {
          font-size: 0.88rem;
          color: var(--text-muted);
          max-width: 280px;
          line-height: 1.5;
          margin: 0;
        }

        .section-title {
          color: var(--text-primary);
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
        }

        .contribution-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .amount-input-group {
          display: flex;
          align-items: center;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 4px;
          overflow: hidden;
        }

        .currency-symbol {
          padding: 0.8rem;
          color: var(--text-secondary);
          background: var(--bg-primary);
          border-right: 1px solid var(--border-color);
        }

        .amount-input-group input {
          border: none;
          flex: 1;
          padding: 0.8rem;
          background: var(--bg-primary);
          color: var(--text-secondary);
        }

        .amount-input-group input:focus {
          outline: none;
        }

        input {
          padding: 0.8rem;
          border: 1px solid var(--border-color);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-secondary);
        }

        input:focus {
          outline: none;
          border-color: var(--accent-color);
        }

        .submit-button {
          background: var(--accent-color);
          color: white;
          border: none;
          padding: 1rem;
          border-radius: 4px;
          font-size: 1rem;
          cursor: pointer;
          transition: background 0.2s;
        }

        .submit-button:hover {
          background: var(--accent-hover);
        }

        .participants-section {
          background: var(--bg-secondary);
          border-radius: 12px;
          box-shadow: 0 2px 4px var(--shadow-color);
          overflow: hidden;
        }

        .section-toggle {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.25rem;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-primary);
          transition: background 0.15s;
          -webkit-tap-highlight-color: transparent;
        }

        .section-toggle:hover { background: var(--bg-primary); }

        .section-toggle-left {
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }

        .section-toggle-icon { font-size: 1.1rem; }

        .section-toggle-title {
          font-weight: 700;
          font-size: 1rem;
          color: var(--text-primary);
        }

        .section-toggle-count {
          background: var(--bg-primary);
          color: var(--text-muted);
          border: 1px solid var(--border-color);
          font-size: 0.75rem;
          font-weight: 600;
          border-radius: 999px;
          padding: 1px 8px;
        }

        .section-toggle-arrow {
          font-size: 1.1rem;
          color: var(--text-muted);
          transition: transform 0.2s;
          display: inline-block;
        }

        .section-toggle-arrow.open { transform: rotate(180deg); }

        .participants-body {
          padding: 0 1.25rem 1.25rem;
          border-top: 1px solid var(--border-color);
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .invite-friends-btn {
          align-self: flex-start;
          background: var(--accent-color);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 0.45rem 1.1rem;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
          margin-top: 0.75rem;
          transition: opacity 0.15s;
        }

        .invite-friends-btn:hover { opacity: 0.85; }

        /* Invite modal */
        .invite-modal {
          background: var(--bg-secondary);
          border-radius: 16px;
          padding: 1.5rem;
          width: 100%;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .invite-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .invite-modal-header h3 {
          margin: 0;
          color: var(--text-primary);
          font-size: 1.1rem;
        }

        .modal-close-btn {
          background: none;
          border: none;
          font-size: 1.1rem;
          color: var(--text-muted);
          cursor: pointer;
          padding: 0.2rem 0.4rem;
          border-radius: 4px;
        }

        .modal-close-btn:hover { background: var(--bg-primary); }

        .invite-empty {
          color: var(--text-muted);
          font-size: 0.9rem;
          text-align: center;
          padding: 1rem 0;
        }

        .invite-list {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        .invite-friend-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0.75rem;
          background: var(--bg-primary);
          border-radius: 10px;
        }

        .invite-friend-name {
          flex: 1;
          color: var(--text-primary);
          font-weight: 500;
          font-size: 0.95rem;
        }

        .invite-send-btn {
          background: var(--accent-color);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.35rem 0.85rem;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.15s;
        }

        .invite-send-btn:hover { opacity: 0.85; }

        .invite-sent-label {
          font-size: 0.82rem;
          color: #10b981;
          font-weight: 600;
        }

        .participant-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .participant-card {
          background: var(--bg-primary);
          padding: 0.75rem;
          border-radius: 12px;
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.5rem;
          box-shadow: 0 2px 8px var(--shadow-color);
          transition: all 0.2s ease;
        }

        @media (min-width: 768px) {
          .participant-card { padding: 1.5rem; flex-wrap: nowrap; }
        }

        .participant-card.current-user {
          background: var(--bg-hover);
          border: 2px solid var(--accent-color);
        }

        .current-user-badge {
          background: var(--accent-color);
          color: white;
          padding: 0.2rem 0.5rem;
          border-radius: 12px;
          font-size: 0.8rem;
          white-space: nowrap;
        }

        .creator-badge {
          background: var(--bg-secondary);
          color: var(--text-muted);
          border: 1px solid var(--border-color);
          padding: 0.15rem 0.45rem;
          border-radius: 10px;
          font-size: 0.72rem;
          white-space: nowrap;
        }

        .participant-avatar-wrap.clickable {
          cursor: pointer;
          transition: opacity 0.15s;
        }

        .participant-avatar-wrap.clickable:hover { opacity: 0.8; }

        .clickable-name {
          cursor: pointer;
          transition: color 0.15s;
        }

        .clickable-name:hover { color: var(--accent-color) !important; }

        .participant-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          min-width: 0;
          flex: 1;
        }

        .participant-name {
          color: var(--text-secondary);
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .participant-email {
          color: var(--text-muted);
          font-size: 0.9rem;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .participant-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          text-align: right;
          min-width: 0;
          flex-shrink: 0;
        }

        .participant-amount {
          font-weight: bold;
          font-size: 1.2rem;
        }

        .participant-amount.negative {
          color: var(--danger-color);
        }

        .participant-amount.positive {
          color: var(--success-color);
        }

        .amount-explanation {
          font-size: 0.85rem;
          color: var(--text-muted);
        }

        .join-date {
          color: var(--text-muted);
          font-size: 0.85rem;
        }

        .empty-message {
          color: var(--text-muted);
          text-align: center;
          padding: 2rem;
        }

        .contributions-section { display: none; /* migrado a tab-content */ }

        @media (min-width: 768px) {
          .contributions-section { padding: 2rem; margin-top: 0; }
        }

        .contributions-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .contribution-card {
          background: var(--bg-primary);
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px var(--shadow-color);
          transition: box-shadow 0.2s ease;
        }

        .contribution-card:hover {
          box-shadow: 0 4px 12px var(--shadow-color);
        }

        .contribution-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .contribution-amount {
          color: var(--accent-color);
          font-size: 1.2rem;
          font-weight: bold;
        }

        .contribution-date {
          color: var(--text-muted);
          font-size: 0.8rem;
        }

        .contribution-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .contributor-row {
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .contributor-name {
          color: var(--text-secondary);
          font-weight: 500;
        }

        .contribution-reason {
          color: var(--text-muted);
          font-style: italic;
          font-size: 0.9rem;
        }

        .header-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          width: 100%;
          margin-top: 0.5rem;
        }

        .header-actions > button {
          flex: 1;
        }

        @media (min-width: 768px) {
          .header-actions {
            width: auto;
            margin-top: 0;
          }

          .header-actions > button {
            flex: none;
          }
        }

        .closed-badge {
          background: #6b7280;
          color: white;
          padding: 0.2rem 0.6rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .shame-wall {
          background: var(--bg-secondary);
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 12px var(--shadow-color);
          border-top: 4px solid #ef4444;
        }

        .shame-title {
          color: var(--text-primary);
          font-size: 1.5rem;
          margin-bottom: 0.25rem;
        }

        .shame-subtitle {
          color: var(--text-muted);
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
        }

        .debts-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .debt-card {
          background: var(--bg-primary);
          border-radius: 8px;
          padding: 1rem 1.25rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          border-left: 3px solid #ef4444;
        }

        .debt-card.debt-paid {
          border-left-color: #22c55e;
          opacity: 0.7;
        }

        .debt-card.debt-mine {
          background: var(--bg-hover);
        }

        .debt-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .debt-borrower {
          font-weight: 600;
          color: #ef4444;
        }

        .debt-arrow {
          color: var(--text-muted);
          font-size: 0.85rem;
        }

        .debt-lender {
          font-weight: 600;
          color: #22c55e;
        }

        .debt-right {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-shrink: 0;
        }

        .debt-amount {
          font-weight: 700;
          font-size: 1.1rem;
          color: var(--text-primary);
        }

        .debt-status-paid {
          color: #22c55e;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .debt-status-waiting {
          font-size: 0.8rem;
          color: #f59e0b;
          font-weight: 500;
        }

        .debt-pending-confirm {
          border-left: 3px solid #f59e0b;
        }

        .debt-actions-col {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.3rem;
        }

        .deny-button {
          background: transparent;
          color: var(--danger-color);
          border: 1px solid var(--danger-color);
          padding: 0.3rem 0.7rem;
          border-radius: 6px;
          font-size: 0.8rem;
          cursor: pointer;
        }

        .debtor-pay {
          background: var(--accent-color) !important;
        }

        .pay-modal {
          background: var(--bg-secondary);
          border-radius: 16px;
          padding: 1.5rem;
          width: 320px;
          max-width: 90vw;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .pay-modal-title {
          margin: 0;
          color: var(--text-primary);
          font-size: 1.05rem;
          font-weight: 700;
        }

        .pay-modal-hint {
          margin: 0;
          font-size: 0.85rem;
          color: var(--text-muted);
        }

        .pay-proof-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.65rem 0.9rem;
          border: 1.5px dashed var(--border-color);
          border-radius: 8px;
          font-size: 0.85rem;
          color: var(--text-secondary);
          cursor: pointer;
          transition: border-color 0.15s;
        }

        .pay-proof-label:hover { border-color: var(--accent-color); }

        .pay-date-input {
          padding: 0.65rem 0.85rem;
          border: 1.5px solid var(--border-color);
          border-radius: 8px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.9rem;
          width: 100%;
          box-sizing: border-box;
        }

        .pay-modal-actions {
          display: flex;
          gap: 0.5rem;
        }

        .pay-modal-btn {
          flex: 1;
          padding: 0.65rem;
          border-radius: 8px;
          border: none;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
        }

        .pay-modal-btn.confirm { background: var(--accent-color); color: white; }
        .pay-modal-btn.cancel { background: var(--bg-primary); color: var(--text-secondary); border: 1px solid var(--border-color); }
        .pay-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .pay-button {
          background: #22c55e;
          color: white;
          border: none;
          padding: 0.4rem 0.9rem;
          border-radius: 6px;
          font-size: 0.85rem;
          cursor: pointer;
          transition: background 0.2s;
        }

        .pay-button:hover {
          background: #16a34a;
        }

        .closed-notice {
          color: #6b7280;
          font-size: 0.9rem;
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: var(--bg-primary);
          border-radius: 6px;
          border-left: 3px solid #6b7280;
        }

        .close-vaquita-button {
          background: #ef4444;
          color: white;
          border: none;
          padding: 0.8rem 1.5rem;
          border-radius: 8px;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .close-vaquita-button:hover:not(:disabled) {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .close-vaquita-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .reopen-vaquita-button {
          background: #f59e0b;
          color: white;
          border: none;
          padding: 0.8rem 1.5rem;
          border-radius: 8px;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .reopen-vaquita-button:hover:not(:disabled) {
          background: #d97706;
          transform: translateY(-2px);
        }

        .reopen-vaquita-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .payment-summary-button {
          background: var(--accent-color);
          color: white;
          border: none;
          padding: 0.8rem 1.5rem;
          border-radius: 8px;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .payment-summary-button:hover {
          background: var(--accent-hover);
          transform: translateY(-2px);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: var(--bg-secondary);
          border-radius: 8px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .modal-header {
          padding: 1rem;
          border-bottom: 1px solid var(--border-color);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h2 {
          color: var(--text-primary);
          margin: 0;
          font-size: 1.2rem;
        }

        .close-button {
          background: none;
          border: none;
          color: var(--text-muted);
          font-size: 2rem;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .close-button:hover {
          color: var(--text-primary);
        }

        .modal-body {
          padding: 0.75rem;
        }

        @media (min-width: 500px) {
          .modal-body { padding: 1.5rem; }
        }

        .summary-explanation {
          margin-bottom: 1.5rem;
          color: var(--text-secondary);
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .payments-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .payment-item {
          background: var(--bg-primary);
          padding: 0.75rem;
          border-radius: 12px;
          box-shadow: 0 4px 12px var(--shadow-color);
          transition: box-shadow 0.2s ease;
        }

        @media (min-width: 500px) {
          .payment-item { padding: 1.5rem; }
        }

        .payment-item:hover {
          box-shadow: 0 6px 16px var(--shadow-color);
        }

        .payment-details {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .payment-users {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        @media (min-width: 500px) {
          .payment-users { flex-direction: row; justify-content: space-between; gap: 1rem; }
        }

        .user-box {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          border-radius: 12px;
          background: var(--bg-secondary);
          width: 100%;
          box-shadow: 0 2px 6px var(--shadow-color);
        }

        .user-box.from-user {
          background: var(--danger-bg);
          border-left: 3px solid var(--danger-color);
        }

        .user-box.to-user {
          background: var(--success-bg);
          border-left: 3px solid var(--success-color);
        }

        .user-box.from-user .payment-label {
          color: var(--danger-color);
        }

        .user-box.to-user .payment-label {
          color: var(--success-color);
        }

        @media (min-width: 500px) {
          .user-box { min-width: 0; flex: 1; width: auto; }
        }

        .user-emoji {
          font-size: 1.5rem;
        }

        .user-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .user-name {
          color: var(--text-primary);
          font-weight: 500;
        }

        .payment-label {
          color: var(--text-muted);
          font-size: 0.8rem;
        }

        .payment-amount-box {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;
        }

        @media (min-width: 500px) {
          .payment-amount-box { flex-direction: column; width: auto; }
        }

        .payment-amount {
          color: var(--accent-color);
          font-weight: bold;
          font-size: 1.1rem;
          background: var(--bg-secondary);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          box-shadow: 0 2px 6px var(--shadow-color);
        }

        .payment-arrow {
          color: var(--text-muted);
          font-size: 1.1rem;
        }

        .summary-footer {
          margin-top: 2rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border-color);
        }

        .summary-note {
          color: var(--text-muted);
          font-size: 0.9rem;
          font-style: italic;
          text-align: center;
        }

        .no-payments {
          text-align: center;
          padding: 3rem 1rem;
          color: var(--text-secondary);
        }

        .sub-text {
          color: var(--text-muted);
          font-size: 0.9rem;
          margin-top: 0.5rem;
        }

        .balance-explanation {
          margin-top: 2rem;
          padding: 1.5rem;
          border-radius: 12px;
          background: var(--bg-primary);
          box-shadow: 0 2px 8px var(--shadow-color);
        }

        .average-amount {
          color: var(--text-primary);
          font-size: 1.1rem;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .average-amount strong {
          color: var(--text-primary);
          font-size: 1.3rem;
        }

        .explanation-title {
          color: var(--text-primary);
          margin-bottom: 1rem;
          text-align: center;
          font-size: 0.95rem;
        }

        .explanation-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .explanation-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border-radius: 8px;
          background: var(--bg-secondary);
        }

        .explanation-item.negative {
          border-left: 4px solid var(--danger-color);
        }

        .explanation-item.positive {
          border-left: 4px solid var(--success-color);
        }

        .amount-example {
          font-weight: bold;
          font-size: 1.1rem;
          min-width: 80px;
        }

        .explanation-item.negative .amount-example,
        .explanation-item.negative .explanation-text {
          color: var(--danger-color);
        }

        .explanation-item.positive .amount-example,
        .explanation-item.positive .explanation-text {
          color: var(--success-color);
        }

        .explanation-text {
          font-size: 0.95rem;
        }

        /* Receipt / boleta */
        .receipt-input-area {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        .file-input {
          padding: 0.5rem;
          border: 2px dashed var(--border-color);
          border-radius: 8px;
          background: var(--bg-primary);
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 0.9rem;
        }

        .file-selected {
          font-size: 0.85rem;
          color: var(--accent-color);
        }

        .receipt-hint {
          font-size: 0.8rem;
          color: #f59e0b;
          margin: 0;
        }

        .contrib-pending {
          border-left: 3px solid #f59e0b;
        }

        .contrib-rejected {
          border-left: 3px solid #ef4444;
          opacity: 0.7;
        }

        .contrib-badge {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.2rem 0.5rem;
          border-radius: 10px;
        }

        .contrib-badge.pending   { background: #fef3c7; color: #92400e; }
        .contrib-badge.confirmed { background: #dcfce7; color: #166534; }
        .contrib-badge.rejected  { background: #fee2e2; color: #991b1b; }

        .receipt-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 0.75rem;
          flex-wrap: wrap;
        }

        .receipt-thumb-btn {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          color: var(--text-secondary);
          padding: 0.35rem 0.8rem;
          border-radius: 6px;
          font-size: 0.85rem;
          cursor: pointer;
          transition: background 0.15s;
        }

        .receipt-thumb-btn:hover { background: var(--bg-hover); }

        .admin-actions {
          display: flex;
          gap: 0.5rem;
        }

        .confirm-btn {
          background: #22c55e;
          color: white;
          border: none;
          padding: 0.35rem 0.9rem;
          border-radius: 6px;
          font-size: 0.85rem;
          cursor: pointer;
        }

        .confirm-btn:hover { background: #16a34a; }

        .reject-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 0.35rem 0.9rem;
          border-radius: 6px;
          font-size: 0.85rem;
          cursor: pointer;
        }

        .reject-btn:hover { background: #dc2626; }

        /* Lightbox */
        .lightbox-content {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        /* QR modal */
        .qr-modal {
          background: var(--bg-secondary);
          border-radius: 16px;
          padding: 1.5rem;
          width: 100%;
          max-width: 340px;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          align-items: center;
        }

        .qr-code-wrap {
          background: white;
          padding: 1rem;
          border-radius: 12px;
        }

        /* Beneficiary selector */
        .beneficiary-toggle {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .toggle-btn {
          padding: 0.4rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: 20px;
          background: var(--bg-primary);
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.15s;
        }

        .toggle-btn.active {
          background: var(--accent-color);
          color: white;
          border-color: var(--accent-color);
        }

        .beneficiary-checkboxes {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          padding: 0.75rem;
          background: var(--bg-primary);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .beneficiary-checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-secondary);
          font-size: 0.9rem;
          cursor: pointer;
        }

        .select-all-label {
          padding-bottom: 0.4rem;
          margin-bottom: 0.2rem;
          border-bottom: 1px solid var(--border-color);
          color: var(--text-primary);
        }

        .beneficiary-chips {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.4rem;
          margin-top: 0.5rem;
        }

        .beneficiary-chips-label {
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .beneficiary-chip {
          background: var(--accent-color);
          color: white;
          font-size: 0.72rem;
          padding: 0.15rem 0.5rem;
          border-radius: 10px;
        }

        .edit-beneficiaries-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.9rem;
          padding: 0.2rem 0.4rem;
          border-radius: 4px;
          transition: background 0.15s;
        }

        .edit-beneficiaries-btn:hover { background: var(--bg-hover); }

        .edit-beneficiaries-panel {
          margin-top: 0.75rem;
          padding: 0.75rem;
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .edit-beneficiaries-title {
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        /* Bank copy button */
        .bank-copy-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 0.4rem 0.9rem;
          border-radius: 6px;
          font-size: 0.82rem;
          cursor: pointer;
          transition: background 0.15s;
          white-space: nowrap;
        }

        .bank-copy-btn:hover { background: #2563eb; }
      `}</style>
    </div>
  );
} 